import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { OutputMessage, ReceptorMessage } from 'src/app/models/common/output.model';
import { Product, ProductService, ProductSnapshot, ProductUI } from 'src/app/shared-lib/domains/product';
import { ShopBlService } from "src/app/shared/services/shopBl.service";
import { Swiper, SwiperOptions } from "swiper/types";

@Component({
  selector: "app-product-list",
  templateUrl: "./product-list.component.html",
  styleUrls: ["./product-list.component.scss"],
  standalone: false
})
export class ProductListComponent
  implements ReceptorMessage, OnInit, OnChanges, AfterViewInit {
  @ViewChild("swiperRef") swiperRef: ElementRef;
  swiper?: Swiper;
  @Input() newDesign!: boolean;
  /**
   * Final list of products to display. When provided, the component behaves as a presentational
   * view and skips the legacy internal fetch. This is the preferred input for new callers.
   */
  @Input() products: Product[] | ProductSnapshot[] | null = null;
  /**
   * Legacy seed input: when `products` is not provided, the component fetches related store
   * products using these as the category seed via `ProductService.getRelatedProducts`.
   * Kept for backwards compatibility with `shop.page` (featured grid) and `popup-before-pay`.
   * New callers should compute the list outside and pass it through `products`.
   */
  @Input() relatedProduct: Product[] | ProductSnapshot[] = [];
  @Input() limit = 5;
  @Input() lateralList = false;
  @Input() measurement = false;
  @Input() onlyFeatured = false;
  @Input() smallLabels = false;
  @Input() swiperConfig: SwiperOptions = {
    spaceBetween: 25,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
    },
    pagination: {
      clickable: true,
      el: ".swiper-pagination",
      type: "bullets",
    },
    navigation: false,
    loop: false,
    keyboard: false,
    slidesPerView: 1,
    allowTouchMove: true,
  };

  productLists: ProductUI[] = [];
  loading = true;
  private swiperInitialized = false;

  constructor(
    private productService: ProductService,
    private shopBlService: ShopBlService,
    private ref: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    // Presentational mode: parent provides the final list.
    if (this.products) {
      this.applyProducts(this.products);
      return;
    }

    // Legacy mode: fetch related products using `relatedProduct` as a category seed.
    await this.loadLegacyRelatedProducts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products'] && !changes['products'].firstChange && this.products) {
      this.applyProducts(this.products);
    }
  }

  async ngAfterViewInit() {
    this.tryInitSwiper();
  }

  private async loadLegacyRelatedProducts() {
    const products = await this.productService.getRelatedProducts(
      this.relatedProduct as Product[],
      this.limit,
    );
    const filtered = this.onlyFeatured ? products.filter(p => p.featured) : products;
    this.applyProducts(filtered);
  }

  private applyProducts(list: Array<Product | ProductSnapshot | ProductUI>) {
    this.productLists = (list ?? []) as unknown as ProductUI[];
    this.loading = false;
    this.ref.markForCheck();
    // Defer swiper init until the view has rendered the slides.
    setTimeout(() => this.tryInitSwiper(), 0);
  }

  private tryInitSwiper() {
    if (this.swiperInitialized || !this.swiperRef?.nativeElement || !this.productLists.length) {
      return;
    }
    const swiperEl = this.swiperRef.nativeElement;
    this.swiper = swiperEl.swiper;
    Object.assign(swiperEl, this.swiperConfig);
    setTimeout(() => swiperEl.initialize(), 10);
    this.swiperInitialized = true;
  }

  /**
   * Emits the first status of the product List
   * @param productCard
   */
  onAdd(productCard) {
    this.shopBlService.selectProduct(productCard, this.measurement);
  }

  // emitAction(type: string, value?: any): void { this.action.emit(new OutputMessage(type,value))}
  onAction(event: OutputMessage): void {
    event.invoke(this);
  }

  autoplayStop(force = false) {
    //TODO J.A.Pendiente de ver si es necesario y cual es la finalidad
    // const swiper = this.swiperComponent.swiperRef;
    // if (swiper.autoplay.running || force) {
    //   swiper.autoplay.stop();
    //   swiper.slideTo(swiper.activeIndex + 1, 300);
    //   const swiperHtml = document.getElementsByClassName('linear-swiper')[0];
    //   if (swiperHtml) swiperHtml.classList.remove('linear-swiper');
    //   this.swiperComponent.updateSwiper({ speed: 300 });
    // }
  }
}
